import React from 'react';
import { Link } from 'react-router-dom';
import BlueCallToAction from '../../components/common/BlueCallToAction';

/**
 * Revenue Cycle Features Page
 * Based on Videxa's Revenue Cycle Insights solution from videxa.co
 */
const RevenueCyclePage = () => {
  const kpiCategories = [
    {
      title: "Financial Management",
      color: "blue",
      metrics: [
        "Net days in A/R",
        "Cash collections as % of net patient service revenue",
        "Cost to collect (overall)",
        "Cost to collect by functional area"
      ]
    },
    {
      title: "Claims",
      color: "green",
      metrics: [
        "Clean claim rate",
        "Late charges as % of total charges"
      ]
    },
    {
      title: "Pre-Billing",
      color: "purple",
      metrics: [
        "Days in total discharged not final billed (DNFB)",
        "Days in final billed not submitted to payer (FBNS)",
        "Days in total discharged not submitted to payer (DNSP)",
        "Total charge lag days"
      ]
    },
    {
      title: "Account Resolution",
      color: "orange",
      metrics: [
        "Aged A/R as % of total billed A/R",
        "Aged A/R by payer group",
        "Remittance denial rate",
        "Denial write-offs as % of net patient service revenue",
        "Bad debt tracking"
      ]
    },
    {
      title: "Patient Access",
      color: "red",
      metrics: [
        "Service authorization rate - Inpatient/Observation",
        "Service authorization rate - Outpatient",
        "Uninsured to third-party conversion rate",
        "Point-of-service cash collections"
      ]
    }
  ];

  const aiCapabilities = [
    {
      number: "1",
      title: "Predictive Forecasting",
      description: "Real-time insights and data create a forecasting system to alert healthcare leaders to shifting trends that may impact business operations.",
      color: "blue"
    },
    {
      number: "2",
      title: "Best Practices Analysis",
      description: "Compare data from multiple facilities to identify and implement best practices that increase efficiency and effectiveness.",
      color: "green"
    },
    {
      number: "3",
      title: "ML-Based Recommendations",
      description: "Recommended actions based on financial insights derived through machine learning algorithms enable immediate, data-driven decisions.",
      color: "purple"
    },
    {
      number: "4",
      title: "Detection & Prevention",
      description: "Early detection of payor mix ratio changes, physician behavior shifts, and volume changes by service lines for proactive management.",
      color: "orange"
    }
  ];

  const operationalAreas = [
    {
      title: "Readmissions Management",
      description: "Predict and prevent readmissions through advanced analytics and patient risk assessment models."
    },
    {
      title: "Claims Management",
      description: "Optimize claims processing with AI-driven validation and automated denial prevention workflows."
    },
    {
      title: "Staffing Management",
      description: "Optimize workforce allocation based on predictive analytics and patient volume forecasting."
    },
    {
      title: "Throughput Management",
      description: "Improve patient flow and operational efficiency through real-time monitoring and analytics."
    }
  ];

  const getColorClasses = (color) => {
    const colors = {
      blue: { bg: 'bg-blue-100 dark:bg-blue-900', text: 'text-blue-600 dark:text-blue-400', border: 'border-blue-500' },
      green: { bg: 'bg-green-100 dark:bg-green-900', text: 'text-green-600 dark:text-green-400', border: 'border-green-500' },
      purple: { bg: 'bg-purple-100 dark:bg-purple-900', text: 'text-purple-600 dark:text-purple-400', border: 'border-purple-500' },
      orange: { bg: 'bg-orange-100 dark:bg-orange-900', text: 'text-orange-600 dark:text-orange-400', border: 'border-orange-500' },
      red: { bg: 'bg-red-100 dark:bg-red-900', text: 'text-red-600 dark:text-red-400', border: 'border-red-500' }
    };
    return colors[color] || colors.blue;
  };

  return (
    <div className="revenue-cycle-page">
      {/* Hero Section */}
      <section className="py-20" style={{
        background: `linear-gradient(to top, #FFC000 0%, #FFFFFF 3%, #FFFFFF 97%, #FFC000 100%)`
      }}>
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6 text-gray-900">
              Revenue Cycle <span style={{ color: '#FFC000' }}>Insights</span>
            </h1>
            <p className="text-xl md:text-2xl mb-4 text-gray-700">
              Industry-leading visibility into your KPIs with real-time actionable insights
            </p>
            <p className="text-lg mb-8 text-gray-600">
              Transform cluttered data into consolidated, actionable intelligence that drives revenue optimization.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/cdi-trial"
                style={{
                  backgroundColor: '#0E3056',
                  color: 'white',
                  padding: '1rem 2rem',
                  borderRadius: '0.5rem',
                  fontWeight: '600'
                }}
                className="hover:opacity-90 transition-opacity"
              >
                Start Free Trial
              </Link>
              <Link
                to="/contact"
                style={{
                  border: '2px solid #0E3056',
                  color: '#0E3056',
                  padding: '1rem 2rem',
                  borderRadius: '0.5rem',
                  fontWeight: '600'
                }}
                className="hover:bg-[#0E3056] hover:text-white transition-colors"
              >
                Contact Sales
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Key Statistics */}
      <section className="py-16 bg-gray-50 dark:bg-gray-900">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-4xl font-bold text-[#0E3056] dark:text-[#FFC000] mb-2">65%</div>
              <p className="text-gray-600 dark:text-gray-300">Reduction in Denial Rates</p>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-[#0E3056] dark:text-[#FFC000] mb-2">22%</div>
              <p className="text-gray-600 dark:text-gray-300">Revenue Cycle Improvement</p>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-[#0E3056] dark:text-[#FFC000] mb-2">94%</div>
              <p className="text-gray-600 dark:text-gray-300">First-Pass Claims Acceptance</p>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-[#0E3056] dark:text-[#FFC000] mb-2">24/7</div>
              <p className="text-gray-600 dark:text-gray-300">Real-time Monitoring</p>
            </div>
          </div>
        </div>
      </section>

      {/* Overview Section */}
      <section className="py-16 bg-white dark:bg-gray-800">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold mb-8 text-gray-900 dark:text-white text-center">
              From Visualization to Insight and Action
            </h2>
            <div className="text-lg text-gray-700 dark:text-gray-300 space-y-6">
              <p>
                It has never been more critical to leverage your data in a transformational way. Reporting is not one size fits all—sometimes you need a high-level view of your business, sometimes more granular detail, and sometimes a holistic view across all systems.
              </p>
              <p>
                <strong>We understand "information overload".</strong> Our solutions are developed with outcomes in mind, helping shorten time to insights to support more intentional decision making and action for Hospitals, Health Systems, and Physician Practices.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Revenue Cycle KPIs Section */}
      <section className="py-16 bg-gray-50 dark:bg-gray-900">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold mb-4 text-gray-900 dark:text-white text-center">
            Revenue Cycle KPIs
          </h2>
          <p className="text-xl text-center text-gray-600 dark:text-gray-300 mb-12 max-w-4xl mx-auto">
            Leveraging MAP Keys—industry-standard metrics used to track your organization's revenue cycle performance with objective, consistent calculations.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {kpiCategories.map((category, index) => {
              const colorClasses = getColorClasses(category.color);
              return (
                <div
                  key={index}
                  className={`bg-white dark:bg-gray-800 p-6 rounded-lg shadow-lg border-t-4 ${colorClasses.border}`}
                >
                  <h3 className={`text-xl font-bold mb-4 ${colorClasses.text}`}>
                    {category.title}
                  </h3>
                  <ul className="space-y-2 text-gray-700 dark:text-gray-300">
                    {category.metrics.map((metric, idx) => (
                      <li key={idx} className="flex items-start">
                        <span className={`mr-2 ${colorClasses.text}`}>•</span>
                        <span>{metric}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* AI and Real-time Data Section */}
      <section className="py-16 bg-white dark:bg-gray-800">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold mb-4 text-gray-900 dark:text-white text-center">
            AI-Powered Analytics
          </h2>
          <p className="text-xl text-center text-gray-600 dark:text-gray-300 mb-12 max-w-4xl mx-auto">
            Creating a new paradigm for healthcare through predictive and prescriptive analytics
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
            {aiCapabilities.map((capability, index) => {
              const colorClasses = getColorClasses(capability.color);
              return (
                <div key={index} className="text-center">
                  <div className={`${colorClasses.bg} p-4 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center`}>
                    <span className={`text-2xl font-bold ${colorClasses.text}`}>{capability.number}</span>
                  </div>
                  <h3 className="text-lg font-bold mb-3 text-gray-900 dark:text-white">{capability.title}</h3>
                  <p className="text-gray-600 dark:text-gray-300 text-sm">
                    {capability.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Operational Analytics Section */}
      <section className="py-16 bg-gray-50 dark:bg-gray-900">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold mb-4 text-gray-900 dark:text-white text-center">
            Operational Analytics
          </h2>
          <p className="text-xl text-center text-gray-600 dark:text-gray-300 mb-12 max-w-4xl mx-auto">
            Improve efficiency and effectiveness of systems used to provide and manage care processes
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {operationalAreas.map((area, index) => (
              <div
                key={index}
                className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-lg text-center"
              >
                <h3 className="text-lg font-bold mb-4 text-[#0E3056] dark:text-[#FFC000]">{area.title}</h3>
                <p className="text-gray-700 dark:text-gray-300 text-sm">
                  {area.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <BlueCallToAction
        heading="Ready to optimize your revenue cycle?"
        description="See how Videxa can transform your healthcare data into actionable insights."
        primaryButtonText="Start Free Trial"
        primaryButtonLink="/cdi-trial"
        secondaryButtonText="Contact Sales"
        secondaryButtonLink="/contact"
      />
    </div>
  );
};

export default RevenueCyclePage;
